import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Controller, useForm, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  completeGoal,
  contributeToGoal,
  createGoal,
  deleteGoal,
  updateGoal,
  withdrawFromGoal,
  type GoalResponse,
  type TrackingMode,
} from '@/api/goals'
import { isPlainAccount, listAccounts } from '@/api/accounts'
import { listMembers } from '@/api/members'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { track } from '@/lib/analytics'
import { DEFAULT_GOAL_ICON, GOAL_ICON_OPTIONS } from '@/lib/goalIconOptions'
import { DatePicker } from '@/components/DatePicker'
import { IconPicker } from '@/components/IconPicker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Radio } from '@/components/ui/radio'
import { Select } from '@/components/ui/select'
import { Sheet } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'

const LABEL = 'mb-1 block text-sm font-medium text-[var(--fl-text-2)]'
const FIELD_ERROR = 'mt-1 text-xs text-[var(--fl-error)]'

function errorDetail(error: unknown): string {
  return (
    (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
    'Something went wrong'
  )
}

function refreshGoals(queryClient: QueryClient, goalId?: string) {
  queryClient.invalidateQueries({ queryKey: ['goals'] })
  // The Dashboard's under-covered-goals figure reads goals too, and these forms open from there.
  queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  if (goalId) {
    queryClient.invalidateQueries({ queryKey: ['goal-contributions', goalId] })
    queryClient.invalidateQueries({ queryKey: ['goal-history', goalId] })
  }
}

function SheetActions({
  submitLabel,
  pending,
  onCancel,
}: {
  submitLabel: string
  pending: boolean
  onCancel: () => void
}) {
  return (
    <div className="flex gap-3 pt-2">
      <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
        Cancel
      </Button>
      <Button type="submit" disabled={pending} className="flex-1 font-semibold">
        {submitLabel}
      </Button>
    </div>
  )
}

function usePlainAccounts() {
  const { data } = useQuery({ queryKey: ['accounts'], queryFn: () => listAccounts() })
  return data?.accounts.filter((a) => isPlainAccount(a.type) && !a.archivedAt) ?? []
}

// ── New / edit goal ──

const goalSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  icon: z.string(),
  targetAmount: z.coerce.number().positive('Target amount must be positive'),
  targetDate: z.string().optional(),
  trackingMode: z.enum(['DEDICATED', 'EARMARK']),
  linkedAccountId: z.string().min(1, 'Account is required'),
  attributedToHouseholdMemberId: z.string().optional(),
  notes: z.string().optional(),
})
type GoalForm = z.infer<typeof goalSchema>

const MODES: { mode: TrackingMode; label: string; hint: string }[] = [
  {
    mode: 'EARMARK',
    label: 'Set aside in an account',
    hint: 'Part of an account’s balance is kept for this goal',
  },
  {
    mode: 'DEDICATED',
    label: 'Own account',
    hint: 'The whole balance of an RD, FD or savings account counts',
  },
]

export function GoalFormSheet({
  goal,
  onClose,
}: {
  goal: GoalResponse | null
  onClose: () => void
}) {
  const { symbol } = useFormatCurrency()
  const queryClient = useQueryClient()
  const accounts = usePlainAccounts()
  const { data: membersData } = useQuery({ queryKey: ['members'], queryFn: () => listMembers() })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<GoalForm>({
    resolver: zodResolver(goalSchema) as Resolver<GoalForm>,
    defaultValues: goal
      ? {
          name: goal.name,
          icon: goal.icon || DEFAULT_GOAL_ICON,
          targetAmount: goal.targetAmount,
          targetDate: goal.targetDate ?? '',
          trackingMode: goal.trackingMode,
          linkedAccountId: goal.linkedAccountId,
          attributedToHouseholdMemberId: goal.attributedToHouseholdMemberId ?? '',
          notes: goal.notes ?? '',
        }
      : { icon: DEFAULT_GOAL_ICON, trackingMode: 'EARMARK' },
  })

  const mutation = useMutation({
    mutationFn: (values: GoalForm) => {
      const payload = {
        ...values,
        targetDate: values.targetDate || undefined,
        attributedToHouseholdMemberId: values.attributedToHouseholdMemberId || undefined,
        notes: values.notes || undefined,
      }
      return goal ? updateGoal(goal.id, payload) : createGoal(payload)
    },
    onSuccess: (_data, values) => {
      if (!goal) track('goal_created', { trackingMode: values.trackingMode })
      refreshGoals(queryClient, goal?.id)
      onClose()
    },
  })

  // A linked account that's since been archived stays selectable on edit, so the form still shows it.
  const linkable =
    goal && !accounts.some((a) => a.id === goal.linkedAccountId)
      ? [...accounts, { id: goal.linkedAccountId, name: goal.linkedAccountName }]
      : accounts
  const members = [
    ...(membersData?.users.map((u) => ({ id: u.id, label: u.displayName ?? u.email })) ?? []),
    ...(membersData?.trackedPeople.map((t) => ({ id: t.id, label: t.name })) ?? []),
  ]
  const locked = goal?.hasContributions ?? false

  return (
    <Sheet title={goal ? 'Edit goal' : 'New goal'} onClose={onClose} className="sm:max-w-lg">
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
        <div className="flex items-end gap-3">
          <div>
            <span className={LABEL}>Icon</span>
            <Controller
              control={control}
              name="icon"
              render={({ field }) => (
                <IconPicker
                  value={field.value}
                  onChange={field.onChange}
                  options={GOAL_ICON_OPTIONS}
                />
              )}
            />
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor="goal-name" className={LABEL}>
              Name
            </label>
            <Input id="goal-name" {...register('name')} placeholder="e.g. Emergency fund" />
          </div>
        </div>
        {errors.name && <p className={FIELD_ERROR}>{errors.name.message}</p>}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="goal-target" className={LABEL}>
              Target ({symbol})
            </label>
            <Input id="goal-target" {...register('targetAmount')} type="number" step="0.01" />
            {errors.targetAmount && <p className={FIELD_ERROR}>{errors.targetAmount.message}</p>}
          </div>
          <div>
            <span className={LABEL}>By (optional)</span>
            <Controller
              control={control}
              name="targetDate"
              render={({ field }) => (
                <DatePicker value={field.value ?? ''} onChange={field.onChange} />
              )}
            />
          </div>
        </div>

        <fieldset>
          <legend className={LABEL}>
            How it’s tracked
            {locked && (
              <span className="ml-2 text-xs font-normal">
                (locked after the first contribution)
              </span>
            )}
          </legend>
          <div className="flex flex-col gap-2">
            {MODES.map(({ mode, label, hint }) => (
              <label
                key={mode}
                className={`flex cursor-pointer items-start gap-2.5 rounded-lg border border-[var(--fl-border)] px-3 py-2.5 text-sm ${locked ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <Radio
                  {...register('trackingMode')}
                  value={mode}
                  disabled={locked}
                  className="mt-0.5"
                />
                <span>
                  <span className="block font-medium text-[var(--fl-text-1)]">{label}</span>
                  <span className="block text-xs text-[var(--fl-text-2)]">{hint}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <span className={LABEL}>Account</span>
          <Controller
            control={control}
            name="linkedAccountId"
            render={({ field }) => (
              <Select
                aria-label="Account"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={locked}
              >
                <option value="">Select account…</option>
                {linkable.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            )}
          />
          {errors.linkedAccountId && (
            <p className={FIELD_ERROR}>{errors.linkedAccountId.message}</p>
          )}
        </div>

        <div>
          <span className={LABEL}>For (optional)</span>
          <Controller
            control={control}
            name="attributedToHouseholdMemberId"
            render={({ field }) => (
              <Select
                aria-label="For"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              >
                <option value="">Whole household</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </Select>
            )}
          />
        </div>

        <div>
          <label htmlFor="goal-notes" className={LABEL}>
            Notes (optional)
          </label>
          <Textarea id="goal-notes" {...register('notes')} rows={2} />
        </div>

        {mutation.isError && (
          <p className="text-sm text-[var(--fl-error)]">{errorDetail(mutation.error)}</p>
        )}
        <SheetActions
          submitLabel={goal ? 'Save' : 'Create goal'}
          pending={isSubmitting || mutation.isPending}
          onCancel={onClose}
        />
      </form>
    </Sheet>
  )
}

// ── Add money ──

const contributeSchema = z.object({
  amount: z.coerce.number().positive('Amount must be positive'),
  sourceAccountId: z.string().min(1, 'Account is required'),
  note: z.string().optional(),
  contributedAt: z.string().optional(),
})
type ContributeForm = z.infer<typeof contributeSchema>

export function ContributeModal({ goal, onClose }: { goal: GoalResponse; onClose: () => void }) {
  const { symbol } = useFormatCurrency()
  const queryClient = useQueryClient()
  const accounts = usePlainAccounts()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ContributeForm>({
    resolver: zodResolver(contributeSchema) as Resolver<ContributeForm>,
    defaultValues: {
      contributedAt: new Date().toISOString().split('T')[0],
      sourceAccountId: goal.trackingMode === 'EARMARK' ? goal.linkedAccountId : '',
    },
  })

  const mutation = useMutation({
    mutationFn: (values: ContributeForm) =>
      contributeToGoal(goal.id, {
        ...values,
        contributedAt: values.contributedAt || undefined,
        note: values.note || undefined,
      }),
    onSuccess: (response) => {
      refreshGoals(queryClient, goal.id)
      if (Number(response.progressPercent) >= 100) {
        toast.success(`You've reached your target for "${goal.name}"! You can mark it done.`)
      }
      onClose()
    },
  })

  return (
    <Sheet title={`Add money · ${goal.name}`} onClose={onClose} className="sm:max-w-md">
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
        <div>
          <label htmlFor="contribute-amount" className={LABEL}>
            Amount ({symbol})
          </label>
          <Input
            id="contribute-amount"
            {...register('amount')}
            type="number"
            step="0.01"
            autoFocus
          />
          {errors.amount && <p className={FIELD_ERROR}>{errors.amount.message}</p>}
        </div>
        <div>
          <span className={LABEL}>From account</span>
          <Controller
            control={control}
            name="sourceAccountId"
            render={({ field }) => (
              <Select
                aria-label="From account"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              >
                <option value="">Select account…</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            )}
          />
          {errors.sourceAccountId && (
            <p className={FIELD_ERROR}>{errors.sourceAccountId.message}</p>
          )}
        </div>
        <div>
          <span className={LABEL}>Date</span>
          <Controller
            control={control}
            name="contributedAt"
            render={({ field }) => (
              <DatePicker value={field.value ?? ''} onChange={field.onChange} />
            )}
          />
        </div>
        <div>
          <label htmlFor="contribute-note" className={LABEL}>
            Note (optional)
          </label>
          <Input id="contribute-note" {...register('note')} />
        </div>
        {mutation.isError && (
          <p className="text-sm text-[var(--fl-error)]">{errorDetail(mutation.error)}</p>
        )}
        <SheetActions submitLabel="Add money" pending={mutation.isPending} onCancel={onClose} />
      </form>
    </Sheet>
  )
}

// ── Withdraw ──

const withdrawSchema = z.object({
  amount: z.coerce.number().positive('Amount must be positive'),
  destinationAccountId: z.string().min(1, 'Account is required'),
  note: z.string().optional(),
})
type WithdrawForm = z.infer<typeof withdrawSchema>

export function WithdrawModal({ goal, onClose }: { goal: GoalResponse; onClose: () => void }) {
  const { symbol } = useFormatCurrency()
  const queryClient = useQueryClient()
  const accounts = usePlainAccounts()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<WithdrawForm>({
    resolver: zodResolver(withdrawSchema) as Resolver<WithdrawForm>,
    defaultValues: {
      destinationAccountId: goal.trackingMode === 'EARMARK' ? goal.linkedAccountId : '',
    },
  })

  const mutation = useMutation({
    mutationFn: (values: WithdrawForm) =>
      withdrawFromGoal(goal.id, { ...values, note: values.note || undefined }),
    onSuccess: () => {
      refreshGoals(queryClient, goal.id)
      onClose()
    },
  })

  return (
    <Sheet title={`Withdraw · ${goal.name}`} onClose={onClose} className="sm:max-w-md">
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
        <div>
          <label htmlFor="withdraw-amount" className={LABEL}>
            Amount ({symbol})
          </label>
          <Input id="withdraw-amount" {...register('amount')} type="number" step="0.01" autoFocus />
          {errors.amount && <p className={FIELD_ERROR}>{errors.amount.message}</p>}
        </div>
        <div>
          <span className={LABEL}>To account</span>
          <Controller
            control={control}
            name="destinationAccountId"
            render={({ field }) => (
              <Select
                aria-label="To account"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              >
                <option value="">Select account…</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            )}
          />
          {errors.destinationAccountId && (
            <p className={FIELD_ERROR}>{errors.destinationAccountId.message}</p>
          )}
        </div>
        {goal.trackingMode === 'EARMARK' && (
          <p className="text-xs text-[var(--fl-text-2)]">
            Leaving it in {goal.linkedAccountName} just frees the money; no money moves.
          </p>
        )}
        <div>
          <label htmlFor="withdraw-note" className={LABEL}>
            Note (optional)
          </label>
          <Input id="withdraw-note" {...register('note')} />
        </div>
        {mutation.isError && (
          <p className="text-sm text-[var(--fl-error)]">{errorDetail(mutation.error)}</p>
        )}
        <SheetActions submitLabel="Withdraw" pending={mutation.isPending} onCancel={onClose} />
      </form>
    </Sheet>
  )
}

// ── Confirmations ──

function ConfirmSheet({
  title,
  body,
  confirmLabel,
  destructive,
  pending,
  onConfirm,
  onClose,
}: {
  title: string
  body: string
  confirmLabel: string
  destructive?: boolean
  pending: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Sheet title={title} onClose={onClose}>
      <p className="mb-6 text-sm text-[var(--fl-text-2)]">{body}</p>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onClose} className="flex-1">
          Cancel
        </Button>
        <Button
          variant={destructive ? 'destructive' : 'default'}
          onClick={onConfirm}
          disabled={pending}
          className="flex-1 font-semibold"
        >
          {confirmLabel}
        </Button>
      </div>
    </Sheet>
  )
}

export function DeleteGoalConfirm({
  goal,
  onClose,
  onDeleted,
}: {
  goal: GoalResponse
  onClose: () => void
  onDeleted?: () => void
}) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => deleteGoal(goal.id),
    onSuccess: () => {
      refreshGoals(queryClient)
      onClose()
      onDeleted?.()
    },
    onError: () => toast.error('Failed to delete goal. Please try again.'),
  })
  return (
    <ConfirmSheet
      title="Delete goal?"
      body={`Delete “${goal.name}”? Its history goes too. The transfers stay in your transactions. This can’t be undone.`}
      confirmLabel="Delete"
      destructive
      pending={mutation.isPending}
      onConfirm={() => mutation.mutate()}
      onClose={onClose}
    />
  )
}

export function CompleteGoalConfirm({
  goal,
  onClose,
}: {
  goal: GoalResponse
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => completeGoal(goal.id),
    onSuccess: () => {
      refreshGoals(queryClient, goal.id)
      onClose()
    },
    onError: () => toast.error('Failed to mark goal as done. Please try again.'),
  })
  return (
    <ConfirmSheet
      title="Mark as done?"
      body={
        goal.trackingMode === 'EARMARK'
          ? `Mark “${goal.name}” as done? The money set aside in ${goal.linkedAccountName} becomes free to spend.`
          : `Mark “${goal.name}” as done? Its progress is kept as it stands today.`
      }
      confirmLabel="Mark done"
      pending={mutation.isPending}
      onConfirm={() => mutation.mutate()}
      onClose={onClose}
    />
  )
}
